// @ts-strict-ignore
import { useMemo } from "react";

import type {
  ErrorOf,
  FetcherOf,
  FilteredDashboardApiEndpointKeys,
  PusherDeveloperBindings,
  QueryKeyOf,
  ResultOf,
  UseDashboardApiQueryProps,
  dashboardApiEndpoints,
} from "@dashboardApi/apis";
import { useQuery } from "@tanstack/react-query";

import { useAppGroupId } from "@services/useAppGroupId";
import { useErrorBoundary } from "@services/useErrorBoundary";
import { useInEditorDomain } from "@services/useInEditorDomain";
import { usePusherContextV2 } from "@services/usePusherContextV2";

import { useDashboardApi } from "./useDashboardApi";
import { useEditorDomainApiPostWebsocketQueryFetcher } from "./useEditorDomainApiPostWebsocketQueryFetcher";
import { delayOptions, jobTokenFromParams } from "./utils";

/**
 * Based on the endpoint specified, this accepts arguments for the underlying GET fetcher function,
 * and options for react-query, and converts them into the query key and arguments to useQuery
 * @template Endpoint the endpoint name. Example: `"/engagement/get_changelogs"`
 * @template GetFetcherFunction an intermediary type to remove duplication in the type, this is the type of the fetcher function for `GET`
 * @template TData this is the return value of `select`, which is inferred from the `options` argument.
 */
export const useDashboardApiPostWebsocketQuery = <
  Endpoint extends FilteredDashboardApiEndpointKeys<typeof dashboardApiEndpoints, "POSTWS">,
  GetFetcherFunction extends FetcherOf<"POSTWS", Endpoint>,
  TFnData = ResultOf<"POSTWS", Endpoint>,
  TData = TFnData,
>(
  endpoint: Endpoint,
  {
    params,
    options,
  }: UseDashboardApiQueryProps<"POSTWS", typeof dashboardApiEndpoints, Endpoint, GetFetcherFunction, TFnData, TData>
) => {
  const { logException } = useErrorBoundary();
  const appGroupId = useAppGroupId();
  const { POSTWS: postwsFetcherFunction } = useDashboardApi(endpoint);
  const pusherContext = usePusherContextV2();
  const inEditorDomain = useInEditorDomain();

  // If we are in the editor domain, we will use the postMessageFetcher to send the request to the parent window.
  // The parent window will then send the request to the backend. This is because the editor domain is not allowed
  // to make network requests directly.
  const postMessageFetcher = useEditorDomainApiPostWebsocketQueryFetcher(endpoint);

  // When in editor domain, pass the custom postMessage fetcher
  const fetcherFunction = (args) => {
    return (postwsFetcherFunction as any)(args, inEditorDomain ? postMessageFetcher : undefined);
  };

  // generate a stable job token from the parameters, including appGroupId to
  // differentiate identical queries across workspaces
  const jobToken = jobTokenFromParams({ ...(params[0] as any), appGroupId });
  const queryKey: QueryKeyOf<"POSTWS", Endpoint> = [
    { cacheKey: endpoint, appGroupId },
    ...[
      {
        ...(params[0] as any),
        jobToken,
      },
      ...(params.slice(1) as any[]),
    ],
  ] as unknown as QueryKeyOf<"POSTWS", Endpoint>;

  const pusher: PusherDeveloperBindings | undefined = useMemo(
    () =>
      inEditorDomain
        ? undefined
        : {
            bind: (eventName, callback) => {
              pusherContext.subscribeChannel({
                channel: "developer",
                eventName,
                callback,
              });
            },
            unbind: (eventName, callback) => {
              pusherContext.unsubscribeChannel({
                channel: "developer",
                eventName,
                callback,
              });
            },
          },
    [pusherContext, inEditorDomain]
  );

  return useQuery<TFnData, ErrorOf<"POSTWS", Endpoint>, TData, QueryKeyOf<"POSTWS", Endpoint>>(
    queryKey,
    fetcherFunction as any,
    {
      ...delayOptions(options || {}),
      staleTime: 60000,
      meta: { pusher, logException },
    }
  );
};
